import React, { useEffect, useState, useCallback } from "react";

import { useNavigate } from "react-router-dom";
import AmountSelector from "./AmountSelector";
import PaymentMethodList from "./PaymentMethodList";
import { apiClient } from "../../api";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { baseUrl } from "../../Environments/environment";
import { toast } from "react-toastify";

export default function AddCredits() {
  const navigate = useNavigate();

  const [wallet, setWallet] = useState(null);
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [amountCents, setAmountCents] = useState(0);
  const [selectedCredits, setSelectedCredits] = useState(0);

  const [amountDisplay, setAmountDisplay] = useState("$0.00");
  const { selectedPropertyId } = useProperty();
  const propertyId = selectedPropertyId;

  const loadPaymentMethods = useCallback(async () => {
    try {
      const response2 = await apiClient.get(
        `${baseUrl}/stripe/payment-methods/${propertyId}`
      );

      setMethods(response2.data.result);

      if (response2.data.result.length > 0) {
        const defaultPm =
          response2.data.result.find((m) => m.isDefault) ||
          response2.data.result[0];

        setSelectedMethod(defaultPm._id);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load payment methods");
    }
  }, [propertyId]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await apiClient.get(
          `${baseUrl}/stripe/wallet/${propertyId}`
        );
        setWallet(response.data.result);

        loadPaymentMethods();
      } catch (err) {
        console.error(err);
      }
    };

    if (propertyId) load();
  }, [propertyId, loadPaymentMethods]);

  const onAmountChange = ({ credits, amountCents, amountDisplay }) => {
    setAmountCents(amountCents);
    setAmountDisplay(amountDisplay);
    setSelectedCredits(credits);
  };

  const payWithSavedCard = async () => {
    if (Number(selectedCredits) < 500) {
      toast.error("Minimum purchase is 500 credits.");
      return;
    }
    if (!selectedMethod) {
      toast.error("Select a saved card or add a new one.");
      return;
    }
    if (!Number.isInteger(Number(selectedCredits))) {
      toast.error("Enter valid credits.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post(`${baseUrl}/stripe/charge-off-session`, {
        propertyId: propertyId,
        amount: amountCents,
        credits: Number(selectedCredits),
        paymentMethodId: selectedMethod,
        customerId: wallet.stripeCustomerId,
        userId: wallet._id,
      });
      console.log({ res });
      // response may be { success } or { requiresAction, paymentIntentClientSecret }
      if (res.data.result.success) {
        navigate("/dashboard/success", {
          state: { creditsAdded: selectedCredits },
        });
      } else {
        navigate("/dashboard/payment-failed", {
          state: { error: res.error || "Payment failed" },
        });
      }
    } catch (err) {
      console.error(err);

      navigate("/dashboard/payment-failed", { state: { error: err.message } });
    } finally {
      setLoading(false);
    }
  };
  const payWithNewCard = async () => {
    if (Number(selectedCredits) < 500) {
      toast.error("Minimum purchase is 500 credits.");
      return;
    }
    if (!Number.isInteger(Number(selectedCredits))) {
      toast.error("Enter valid credits.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get(
        `${baseUrl}/stripe/gen-sign?amountCents=${amountCents}`
      );
      console.log({ res });

      const sign = res.data.result.sign;
      if (sign) {
        navigate(`/dashboard/checkout?amount=${amountCents}&sign=${sign}`);
      }
    } catch (err) {
      console.error(err);

      toast.error(err.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };
  const makeDefault = async (methodId) => {
    try {
      await apiClient.put(`${baseUrl}/stripe/payment-method/default`, {
        paymentMethodId: methodId,
        propertyId: propertyId,
      });

      toast.success("Default payment method updated!");
      loadPaymentMethods(); // Refresh list
    } catch (err) {
      console.error(err);
      toast.error("Failed to update default card");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Add Credits</h1>
        <button
          onClick={() => navigate("/dashboard/wallet")}
          className="px-4 py-2 border rounded-md"
        >
          Back to Wallet
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 border rounded-md bg-white">
          <AmountSelector
            onChange={onAmountChange}
            initialCredits={wallet?.topupCredit || 500}
          />
          <div className="mt-4 text-sm text-gray-700">
            Total: <span className="font-medium">{amountDisplay}</span>
          </div>
        </div>

        <div className="p-4 border rounded-md bg-white">
          <h3 className="font-medium mb-3">Payment Methods</h3>
          <PaymentMethodList
            methods={methods}
            selectedId={selectedMethod}
            onSelect={setSelectedMethod}
            onMakeDefault={makeDefault}
          />
          <div className="mt-4 flex gap-2">
            <button
              onClick={payWithNewCard}
              className="px-4 py-2 border rounded-md"
              disabled={loading}
            >
              {loading ? "Processing..." : "Add New Card"}
            </button>

            <button
              onClick={payWithSavedCard}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md"
              disabled={loading || !selectedMethod}
            >
              {loading ? "Processing..." : "Pay with saved card"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
